<template>
  <HeaderFullpage
    desctop
    :title="t('animalView.title')"
  />
  <PageLoader v-if="isLoading" />
  <LayoutBase
    v-else
    gap="30"
  >
    <div class="names">
      <div class="name">
        {{ pet.payload.name }}
      </div>
      <div class="breed-wrapper">
        <IconBase
          :path="`animals/${getAnimalById(pet?.payload?.animal_id || pet?.animal_id).icon}.svg`"
          style="width: 20px;"
        />
        <div class="breed">
          {{ pet.payload.breed }}
        </div>
      </div>
    </div>

    <RouterLink
      class="calendar-link"
      :to="`/calendar?petId=${route.params.id}`"
    >
      <ItemBase>
        {{ t('calendarView.title') }}
      </ItemBase>
    </RouterLink>

    <ItemBase :title="t('animalView.info')">
      <div class="info">
        <div
          v-if="pet.payload.sex"
          class="info-item"
        >
          <div class="info-label">
            {{ t('common.sex') }}
          </div>
          <div class="info-value">
            {{ getSex(pet.payload.sex) }}
          </div>
        </div>
        <div
          v-if="pet.payload.age"
          class="info-item"
        >
          <div class="info-label">
            {{ t('common.age') }}
          </div>
          <div class="info-value">
            {{ fromDateToAge(pet.payload.age) }}
          </div>
        </div>
        <div
          v-if="pet.payload.weight"
          class="info-item"
        >
          <div class="info-label">
            {{ t('common.weight') }}
          </div>
          <div class="info-value">
            {{ pet.payload.weight }} {{ t('common.kg') }}
          </div>
        </div>
      </div>
      <div class="block">
        <div class="block-item">
          <div class="block-label">
            {{ t('animalView.castration') }}
          </div>
          <div class="block-value">
            {{ pet.payload.castration ? t('common.yes') : t('common.no') }}
          </div>
        </div>
        <div
          v-if="pet.payload.lastCheckup"
          class="block-item"
        >
          <div class="block-label">
            {{ t('animalView.checkup') }}
          </div>
          <div class="block-value">
            {{ pet.payload.lastCheckup }}
          </div>
        </div>
        <div class="block-item">
          <div class="block-label">
            {{ t('animalView.chip') }}
          </div>
          <div class="block-value">
            {{ pet.payload.chip ? t('common.yes') : t('common.no') }}
          </div>
        </div>
      </div>
    </ItemBase>

    <ItemBase
      v-if="pet.payload.vaccinations.length"
      :title="t('animalView.vac')"
    >
      <div class="block">
        <div
          v-for=" vac in pet.payload.vaccinations "
          class="block-item"
        >
          <div class="block-label">
            {{ vac.type }}
          </div>
          <div class="block-value">
            {{ vac.createdAt }}
          </div>
        </div>
      </div>
    </ItemBase>

    <ItemBase
      v-if="pet.payload.photos.length"
      :title="t('animalView.photo')"
    >
      <PhotoGallery
        v-model="pet.payload.photos"
        only-view
      />
    </ItemBase>

    <ItemBase
      v-if="pet.payload.chronicIlls"
      :title="t('animalView.chronic')"
    >
      <TextareaBase
        v-model="pet.payload.chronicIlls"
        :rows="6"
        disable
      />
    </ItemBase>
  </LayoutBase>
  <FollowElements hide-drag>
    <BackButton :fixed="position === 'top'" />
  </FollowElements>
  <AnimalPhoto :path="pet.payload?.photos[0]?.path" />
</template>

<script setup>
import { BackButton, ItemBase, TextareaBase, IconBase } from '@/UI';
import { PhotoGallery } from '@/components/PhotoGallery'
import { FollowElements } from '@/components/PaneMain';
import { usePane } from '@/components/PaneMain';
import { AnimalPhoto } from './components/AnimalPhoto'
import { LayoutBase } from '@/components/Layouts';
import { getAnimalInfo } from './api'
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router'
import { fromDateToAge } from '@/helpers/fromDateToAge'
import { getSex } from '@/helpers/getSex'
import { PageLoader } from '@/components/PageLoader'
import { HeaderFullpage } from '@/UI';
import { useI18n } from 'vue-i18n'
import { getAnimalById } from '@/helpers/helpers';

const { t } = useI18n()

const route = useRoute()
const isLoading = ref(true)
const pet = ref({})
const { position } = usePane()

const mapper = (animal) => {
  animal.payload.vaccinations = animal.payload.vaccinations.filter(el => el.createdAt || el.type)
  return animal
}

const init = async () => {

  const { id } = route.params
  const { animal } = await getAnimalInfo({ id })
  pet.value = Object.assign(pet.value, (mapper(animal[0])));
  isLoading.value = false
}

onMounted(() => {
  init()
})
</script>

<style lang="scss" scoped>
[theme="dark"] {
    .info-item {
        background: #37363C;
    }

    .info-value {
        color: #B0B2C2;
    }

    .info-label {
        color: #fff;
    }

    .block {
        background: #37363C;
    }

    .block-item {
        border-bottom: 1px solid #56555C;

        &:last-child {
            border-bottom: none;
        }
    }

    .block-label {
        color: #B0B2C2;
    }

    .block-value {
        color: #B0B2C2;
    }

    .name {
        color: #fff;
    }

    .breed {
        color: #B0B2C2;
    }
}

.block {
    border-radius: 20px;
    background: #FFF;
    box-shadow: 0px 3px 15px 0px rgba(0, 0, 0, 0.05);
    padding: 0 20px;

    &-item {
        border-bottom: 1px solid #E6EAF3;

        &:last-child {
            border-bottom: none;
        }

        align-items: center;
        padding: 25px 0;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 5px;
    }

    &-label {
        color: #5B5F7C;
        font-size: 15px;
        font-weight: 700;
        line-height: 14px;
    }

    &-value {
        color: #8E92AF;
        font-size: 15px;
        font-weight: 700;
        line-height: 14px;
    }
}

.name {
    color: #5B5F7C;
    font-size: 22px;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
    margin-top: 45px;
}

.breed-wrapper {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
}

.breed {
    color: #8E92AF;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
}

.names {
    display: grid;
    grid-template-columns: minmax(0, auto);
    gap: 10px;
}

.info-item {
    padding: 18px 0;
    border-radius: 20px;
    background: #FFF;
    box-shadow: 0px 3px 15px 0px rgba(0, 0, 0, 0.05);
    display: grid;
    grid-template-columns: minmax(0, auto);
    gap: 6px;
}

.info-label {
    color: #8E92AF;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
}

.info-value {
    color: #5B5F7C;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    line-height: 14px;
}

.info {
    display: grid;
    grid-auto-flow: column;
    gap: 10px;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
}

.calendar-link {
    display: block;
    text-decoration: none;

    :deep(.wrapper) {
        border-radius: 20px;
        background: #FFF;
        box-shadow: 0px 3px 15px 0px rgba(0, 0, 0, 0.05);
        padding: 20px;
        color: #5B5F7C;
        font-size: 15px;
        font-weight: 700;
        text-align: center;
    }
}

[theme="dark"] .calendar-link :deep(.wrapper) {
    background: #37363C;
    color: #fff;
}
</style>
